import { SubscribeDtoTypeEnum } from 'api/generated';
import React, { FC, useEffect } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import { PaymentOptions } from 'store/ducks/courses/types';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

interface Props {
  isResponsive?: boolean;
  mainColor: string | undefined;
  goldPurchasePrice?: any;
  goldInstallmentPrice?: any;
  subscribeType?: SubscribeDtoTypeEnum;
  isBundle?: boolean;
  courseType?: string;
  indexPlan?: number;
}

const PaymentOptionsSelector: FC<Props> = ({
  isResponsive,
  mainColor,
  goldPurchasePrice,
  goldInstallmentPrice,
  subscribeType,
  isBundle,
  courseType,
  indexPlan,
}) => {
  const dispatch = useAppDispatch();
  const paymentOption = useAppSelector(selectors.courses.selectPaymentOption);
  useEffect(() => {
    if (!paymentOption) {
      dispatch(actions.courses.setPaymentOption(PaymentOptions.ONETIME));
    }
  }, [dispatch, paymentOption]);
  const savings = goldInstallmentPrice - goldPurchasePrice;

  const handleOptionChange = (option: string) => {
    dispatch(actions.courses.setPaymentOption(option));
  };

  // Function to determine if this is a bundle course
  const isBundleCourse = () => {
    return courseType === 'bundle' || isBundle;
  };

  const accentColor =
    typeof mainColor === 'string' && mainColor && !mainColor.includes('linear-gradient') ? mainColor : '#ed5a26';

  return (
    <RadioButtonContainer isResponsive={isResponsive}>
      {/* Show bundle text only in the first column for bundle courses */}
      {indexPlan === 0 && isBundleCourse() && (
        <BundleDescriptionText accentColor={accentColor}>
          The following study features apply to both the IPTA CPT and Nutrition Academy CNS certifications
        </BundleDescriptionText>
      )}
      {!isResponsive && <RadioContainerTitle mainColor={mainColor}>Enroll / Purchase</RadioContainerTitle>}
      <RadioInputContainer>
        <RadioInput
          type="radio"
          id="payment_plan"
          name="payment"
          style={{ transform: 'scale(1.5)', marginRight: '16px' }}
          value={'plan'}
          checked={paymentOption === 'paymentPlan'}
          onChange={() => handleOptionChange(PaymentOptions.PAYMENT_PLAN)}
        />
        <CustomRadio mainColor={mainColor} isChecked={paymentOption === PaymentOptions.PAYMENT_PLAN}></CustomRadio>
        {'Payment plan'}
      </RadioInputContainer>
      <RadioParagraph>Pay Over Time with 12 Equal Monthly Installments</RadioParagraph>

      <RadioInputContainer>
        <RadioInput
          type="radio"
          id="onetime"
          name="payment"
          style={{ transform: 'scale(1.5)', marginRight: '16px' }}
          value={'onetime'}
          checked={paymentOption === 'onetime'}
          onChange={() => handleOptionChange('onetime')}
        />
        <CustomRadio mainColor={mainColor} isChecked={paymentOption === PaymentOptions.ONETIME}></CustomRadio>
        {'One time payment'}
      </RadioInputContainer>
      {subscribeType === SubscribeDtoTypeEnum.Gold && (
        <RadioParagraph>
          {'Save '}
          <strong>{`$${savings}`}</strong>
          {' With Our One-Time Payment Discount'}
        </RadioParagraph>
      )}
      {subscribeType !== SubscribeDtoTypeEnum.Gold && (
        <RadioParagraph>
          {'Save Money'}
          {' With Our One-Time Payment Discount'}
        </RadioParagraph>
      )}
    </RadioButtonContainer>
  );
};

export default PaymentOptionsSelector;

const RadioButtonContainer = styled.div<{ isResponsive: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  position: ${({ isResponsive }) => (isResponsive ? 'unset' : 'absolute')};
  top: 360px;
  left: 30px;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const RadioContainerTitle = styled.h4<{ mainColor: string | undefined }>`
  ${({ theme: { typography } }) => typography.title_5_bold_20}
  color: ${({ mainColor }) => `${mainColor}`};
`;

const RadioInputContainer = styled.label`
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  ${({ theme: { typography } }) => css`
    ${typography.headline_semibold_18}

    ${respondToWidth.s`
      ${typography.body_basic_semibold_14}
  `}
  `};
`;

const RadioInput = styled.input`
  display: none;
`;

const CustomRadio = styled.span<{ isChecked: boolean; mainColor: string | undefined }>`
  width: 24px;
  height: 24px;
  border: 2px solid #cfcfcfed;
  border-radius: 50%;
  margin-right: 8px;
  position: relative;
  transition: background-color 0.3s, border-color 0.3s;

  ${({ isChecked, mainColor }) =>
    isChecked &&
    css`
      background-color: #cfcfcfed;
      border-color: #cfcfcfed;

      &::after {
        content: '';
        position: absolute;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${mainColor};
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    `}
`;

const RadioParagraph = styled.p`
  ${({ theme: { typography } }) => typography.body_basic_regular_14}
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
  width: 240px;
  margin-left: 32px;
  margin-top: 9px;

  strong {
    font-weight: 900;
  }
`;

const BundleDescriptionText = styled.div<{ accentColor: string }>`
  ${({ theme: { typography } }) => typography.body_large_semibold_16};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  text-align: center;
  padding: 12px;
  background-color: ${({ theme: { colors } }) => colors.neutrals[10]};
  border-radius: 8px;
  border-left: 4px solid ${({ accentColor }) => accentColor};
  font-size: 14px;
  width: 240px;
  position: absolute;
  top: -170px;
`;
